import type { ResponseStatus } from '@/core/api/types';
import type { FormAutomaticProcessingCode, ReportForm } from './reportForm';
import type { ReportOrganizationStatus } from '@/constants/reportOrganizationStatuses';
import type { EoForm } from './eoForm';
import type { FormKind } from '@/components/report-forms/constants/formType';
import type { AccountingType } from '@/constants/accountingType';
import type { ReportDataModel } from '@/constants/reportDataModel';

export type ReportDetails = {
  accountingType: AccountingType;
  dataModel: ReportDataModel;
  jsonForms: ReportForm[];
  aopForms: ReportForm[];
  id: number;
  name: string;
  reportTypePeriodId: number;
  organizationStatusId: number;
  organizationStatusChangedDate: string | null;
  organizationId: number;
  organizationBudgetIdNumber: string;
  organizationTaxIdNumber: string;
  organizationCompanyNumber: string;
  organizationName: string;
  organizationNameTranslit: string | null;
  organizationPlace: string;
  organizationPlaceTranslit: string | null;
  organizationTreasury: number;
  dateFrom: string;
  dateTo: string;
  createdUserId: number;
  createdDate: string;
  approvedUserId: number | null;
  approvedDate: string | null;
  canceledUserId: number | null;
  canceledDate: string | null;
  modifiedUserId: number | null;
  modifiedDate: string | null;
  status: number;
  description: string;
  organizationStatus: ReportOrganizationStatus;
  maxAllowedReportsPerTypeAndPeriod: number;
  periodFrom: string;
  periodTo: string;
  periodStatus: number;
  automaticProcessingCodeVersionId: number;
  reportTypeId: number;
  reportType: string;
  reportTypeFrequencyId: number;
  reportTypeScheduleVersionId: number;
  hasAdvisoryValidations: boolean;
  organizationGroup: number;
  validationFailedCount: number;
  requiredValidationFailedCount: number;
  link: string | null;
  selected: boolean;
  totalYearlyInstances: number;
  yearlyInstanceNumber: number;
};

export type GetReportResponse = {
  report: ReportDetails;
};

export type ApproveReport = {
  file: File | null;
  reportName: string;
  description: string;
  title: string;
  extension: string;
  size: number;
};

export type ReportHelpingRequest = {
  reportName: string;
  reportStatus: number;
};

export type CreateAggregatedReport = {
  formId: number | null;
  includeCurrentValues: boolean;
  aggregatePerProjects: boolean;
  reportsToAggregate: string[];
  reportFormAggregationType: number;
  reportName: string;
};

export type CreateAggregatedReportResponse = {
  forms: ReportForm[];
  reportName: string;
  error: string | null;
};

export type UpdateReportPayload = ImportReport | UpdateReportFormCode | UpdateReportJsonForm;

export type ImportReport = {
  header: ImportReportHeader;
  forms?: Record<string, number[]>[];
};

export type UpdateReportFormCode = {
  header: ReportDetails;
  forms?: ImportReportForm[];
};

export type UpdateReportJsonForm = {
  header: ReportDetails;
  forms?: ImportReportJsonForm[];
};

export type CreateReportResponse = {
  report: ReportDetails;
  forms: CreateReportForm[];
};

export type UpdateJsonFormResponse = {
  report: ReportDetails;
  forms: UpdateJsonForm[];
};

export type CreateReportForm = {
  header: CreateReportHeader;
  codes: CreateReportFormCode[];
  positions: string[];
};

export type CreateReportHeader = {
  type: number;
  program: string | null;
  project: string | null;
  formKind: FormKind;
};

export type CreateReportFormCode = {
  amounts: number[];
  automaticProcessingCode: string;
  importError: ResponseStatus;
};

export type ImportReportResponse = { report: ReportDetails; forms: ImportedReportForm[] };

export type ImportReportHeader = {
  kjsName?: string | null;
  parentKjsName?: string | null;
  name?: string | null;
  organizationStatus?: string;
  organizationStatusChangedDate?: string | null;
  description?: string;
  reportTypePeriodId?: number;
  reportingPeriod?: string | null;
  approvedDate?: string | null;
  link?: string | null;
};

export type ImportReportForm = { header: ImportedReportFormHeader } & Partial<
  Record<number, number[]>
>;

export type FileImportReportJsonForm = {
  header: ImportedReportJsonFormHeader;
  positions: (EoForm | FormAutomaticProcessingCode)[];
};

export type ImportReportJsonForm = {
  header: {
    type: number;
    program?: string | null;
    project?: string | null;
    positions: EoForm[];
  };
};

export type UpdateJsonForm = {
  header: ReportDetails;
  positions: string[];
  codes?: CreateReportFormCode[];
};

export type ImportReportJson = { header: ImportReportHeader; forms: ImportReportForm[] };

export type ImportReportFormCode = {
  code: number;
  amounts: number[];
};

export type ImportedReportForm = {
  header: ImportedReportFormHeader;
  forms: ImportReportForm[];
};

export type ImportedReportFormHeader = {
  program: string | null;
  project: string | null;
  type: number;
};

export type ImportedReportJsonFormHeader = {
  type: number;
  kind: FormKind;
};

export type ImportedReportFormCode = {
  automaticProcessingCode: string;
  amounts: number[];
  importError: string | null;
};

export enum ReportValidity {
  VALID = 1,
  INVALID = 2,
  ADVISORY_INVALID = 3
}

export type ReportValidationData = {
  validationFailedCount: number;
  requiredValidationFailedCount: number;
  status: ReportValidity;
};
